// Global Instructions Rule Applied!
import { ProspectFields } from './fieldConfigs.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import {
  ACTION_TYPE_UPSERT,
  FIELD_KEY_PROG,
  FIELD_KEY_ACTION,
  FIELD_KEY_LABEL,
  TABLE_CONTACTS
} from '../../../state_manager/globals.ts'
import type { ProspectRecord } from '../../../state_manager/data_models/prospect_record.ts'
// Helper function to transform contact to user entry (copied from deals)
function transformContactToUserEntry(contact: any): any | null {
  if (!contact) return null

  if (typeof contact === 'string') {
    return null
  }

  const isNew = contact.value === 'new' || (contact.label && contact.label.includes('(new)'))
  let contactId: string | null = null

  if (isNew) {
    contactId = crypto.randomUUID()
  } else {
    if (contact.value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(contact.value)) {
      contactId = contact.value
    } else if (
      contact[UserEntryFields.contacts__id] &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(contact[UserEntryFields.contacts__id])
    ) {
      contactId = contact[UserEntryFields.contacts__id]
    } else if (contact.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(contact.id)) {
      contactId = contact.id
    }
  }

  const contactData = contact.data || {}
  let firstName: string | null = null
  let lastName: string | null = null

  if (isNew) {
    const nameSource = contact.data?.originalSearch || contact.label || ''
    const nameParts = nameSource
      .replace(/\s*\(new\)\s*$/i, '')
      .trim()
      .split(/\s+/)
      .filter((p: string) => p.length > 0)
    if (nameParts.length === 1) {
      firstName = null
      lastName = nameParts[0]
    } else if (nameParts.length > 1) {
      firstName = nameParts[0]
      lastName = nameParts.slice(1).join(' ')
    }
  } else {
    firstName = contact.first_name || contactData.first_name || contact[UserEntryFields.contacts__first_name] || null
    lastName = contact.last_name || contactData.last_name || contact[UserEntryFields.contacts__last_name] || null
  }

  const rawDisplayName =
    contact.label ||
    contact.full_name ||
    contactData.display_name ||
    contact[UserEntryFields.prog__display_name] ||
    null
  const displayName = rawDisplayName ? rawDisplayName.replace(/\s*\(new\)\s*$/i, '').trim() : null

  const PROG_CONTACTS_ACTION = `${FIELD_KEY_PROG}${TABLE_CONTACTS}${FIELD_KEY_ACTION}`

  const userEntry: any = {
    [UserEntryFields.contacts__id]: contactId,
    [UserEntryFields.contacts__email]:
      contact.email || contactData.email || contact[UserEntryFields.contacts__email] || null,
    [UserEntryFields.contacts__first_name]: firstName,
    [UserEntryFields.contacts__last_name]: lastName,
    [UserEntryFields.contacts__preferred_name]:
      contact.preferred_name || contactData.preferred_name || contact[UserEntryFields.contacts__preferred_name] || null,
    [UserEntryFields.prog__display_name]: displayName
  }

  if (isNew) {
    userEntry[PROG_CONTACTS_ACTION] = ACTION_TYPE_UPSERT
  } else {
    userEntry[PROG_CONTACTS_ACTION] = ''
  }

  return userEntry
}

/**
 * Transform investor/account field from object to string ID
 */
function transformInvestorField(investorValue: any): string | null {
  if (!investorValue) return null

  if (typeof investorValue === 'object') {
    if (investorValue.value) {
      return investorValue.value
    } else if (investorValue.id) {
      return investorValue.id
    }
    return null
  }

  if (investorValue === '' || (typeof investorValue === 'string' && investorValue.trim() === '')) {
    return null
  }

  return typeof investorValue === 'string' ? investorValue : String(investorValue)
}

/**
 * Transform city field from object to code
 */
function transformCityField(cityValue: any): number | null {
  if (!cityValue) return null

  if (typeof cityValue === 'object') {
    if (cityValue.code) {
      return cityValue.code
    } else if (cityValue.value) {
      return cityValue.value
    }
    return null
  }

  if (cityValue === '' || (typeof cityValue === 'string' && cityValue.trim() === '')) {
    return null
  }

  return cityValue
}

/**
 * Normalize numeric string fields to numbers
 */
function normalizeNumericFields(prospectData: Record<string, any>): Record<string, any> {
  const numericFields = [
    ProspectRecordFields.prospects__commitment,
    ProspectRecordFields.prospects__allocation_available
  ]

  const normalized = { ...prospectData }

  numericFields.forEach((field) => {
    if (normalized[field] !== undefined && normalized[field] !== null) {
      if (typeof normalized[field] === 'string') {
        const parsed = parseFloat(normalized[field])
        normalized[field] = isNaN(parsed) ? null : parsed
      }
    }
  })

  return normalized
}

/**
 * Clean empty fields - convert empty strings to null
 */
function cleanEmptyFields(data: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {}

  Object.keys(data).forEach((key) => {
    const value = data[key]
    if (value === '' || (typeof value === 'string' && value.trim() === '')) {
      cleaned[key] = null
    } else if (value !== undefined) {
      cleaned[key] = value
    }
  })

  return cleaned
}

/**
 * Transform form data to ProspectRecord format for API submission
 */
export function transformFormDataToProspectRecord(
  formData: Record<string, any>,
  formValues: Record<string, any>,
  selectedContacts: Record<string, any[]>,
  isNewInvestor: boolean
): ProspectRecord {
  // Merge formData and formValues
  const allFormValues = { ...formData, ...formValues }

  // Extract city label BEFORE filtering (city might be stored as 'cityCode' which isn't in ProspectRecordFields)
  const cityCodeField = AccountRecordFields.accounts__business_city_code
  const cityLabelField = `${AccountRecordFields.accounts__business_city_code}${FIELD_KEY_LABEL}`

  let extractedCityLabel: string | null = null

  // Check for city object in allFormValues (might be stored as 'cityCode' from valueTransformer)
  const cityObject = allFormValues[cityCodeField] || allFormValues.cityCode

  if (cityObject && typeof cityObject === 'object') {
    extractedCityLabel = cityObject.name || cityObject.label || null
  } else if (allFormValues[cityLabelField]) {
    extractedCityLabel = allFormValues[cityLabelField]
  }

  // Get valid ProspectRecordFields keys
  const validProspectFields = new Set<string>(Object.values(ProspectRecordFields))

  // Build prospect record with only valid ProspectRecordFields constants
  let prospectData: Record<string, any> = {}

  // Only include fields that are in ProspectRecordFields
  Object.keys(allFormValues).forEach((key) => {
    if (validProspectFields.has(key)) {
      prospectData[key] = allFormValues[key]
    }
  })

  // Generate UUID for new prospects if not present
  if (!prospectData[ProspectRecordFields.prospects__id]) {
    prospectData[ProspectRecordFields.prospects__id] = crypto.randomUUID()
  }

  // Clean empty fields
  prospectData = cleanEmptyFields(prospectData)

  // Transform investor field from object to string ID
  if (prospectData[ProspectRecordFields.prospects__investor] !== undefined) {
    prospectData[ProspectRecordFields.prospects__investor] = transformInvestorField(
      prospectData[ProspectRecordFields.prospects__investor]
    )
  }

  // Transform city field if present and ensure label is included
  const cityValue = prospectData[cityCodeField] || cityObject

  if (cityValue !== undefined) {
    // Set label if we extracted it earlier
    if (extractedCityLabel) {
      prospectData[cityLabelField] = extractedCityLabel
    }

    // Transform city code to number
    if (cityValue && typeof cityValue === 'object') {
      const cityCode = cityValue.value || cityValue.code || cityValue
      prospectData[cityCodeField] = transformCityField(cityCode)
    } else {
      prospectData[cityCodeField] = transformCityField(cityValue)
    }
  }

  // Normalize numeric fields
  prospectData = normalizeNumericFields(prospectData)

  // Handle new investor creation
  if (isNewInvestor) {
    // Generate UUID for new account
    const accountId = crypto.randomUUID()
    prospectData[AccountRecordFields.accounts__id] = accountId
    prospectData[ProspectRecordFields.prospects__investor] = accountId

    // Set account fields with action type
    const PROG_ACCOUNTS_ACTION = `${FIELD_KEY_PROG}accounts${FIELD_KEY_ACTION}`
    prospectData[PROG_ACCOUNTS_ACTION] = ACTION_TYPE_UPSERT
  }

  // Handle contact relationships
  const introducedByContacts =
    selectedContacts[ProspectFields.contact_accounts__introduced_by] || selectedContacts.introduced_by || []
  const sourcedByContacts =
    selectedContacts[ProspectFields.contact_accounts__sourced_by] || selectedContacts.sourced_by || []
  const leadContacts = selectedContacts[ProspectFields.contact_prospects__lead] || selectedContacts.lead || []

  prospectData[ProspectRecordFields.contact_accounts__introduced_by] = introducedByContacts
    .map(transformContactToUserEntry)
    .filter((entry) => entry !== null)

  prospectData[ProspectRecordFields.contact_accounts__sourced_by] = sourcedByContacts
    .map(transformContactToUserEntry)
    .filter((entry) => entry !== null)

  prospectData[ProspectRecordFields.contact_prospects__lead] = leadContacts
    .map(transformContactToUserEntry)
    .filter((entry) => entry !== null)

  return prospectData as ProspectRecord
}
